/**
 * `useNextSteps` — gathers the facts for the lead's next-steps panel on the
 * current route and hands them to the pure `buildNextSteps` (#1921).
 *
 * **Same hooks, same cache.** Every fact comes from the query hook the
 * surface it points at already uses, with that surface's own key and its
 * own enabled / lead gates, so opening the panel (or rendering the handle's
 * badge) shares those reads instead of adding new ones — no new endpoints:
 *
 *   | Row | Source |
 *   |---|---|
 *   | days short on published days | `useCoverageSummary` → the roster read's `shortfalls_by_day` |
 *   | (aside) issues to review | `useValidationSummary` — **only while the panel is open** |
 *   | leave requests to review | `useLeaveReviewQueue` (this schedule; on a team page, the Review tab's default period) |
 *   | swaps that need your confirm | `useCycleSwaps` `state_summary.accepted` (the `Swaps · N` chip's read, same `swap_requests` gate) |
 *   | unpublished edits | `lib/unpublishedEdits` (what the heatmap reports), else the roster read's `unpublished_count` |
 *   | the team has not been told | the cycle payload via `rosterReleaseState` (the header line's logic) |
 *   | pins that can't be placed | `useAllocationGaps` `open_count` + the first open gap |
 *   | imported calls waiting for a period | `useUnboundCalls` (team pages; the waiting-calls door's read) |
 *   | no schedules yet | the team's periods list (team pages; the Schedule section's read) |
 *   | messages from members | `useTeamMessages` `unread_count` + the unread senders (#2077, #2071; the People page's messages list read) |
 *   | submitted so far | `useSubmissionProgress` — only for a period that collects submissions |
 *
 * **Cost rule.** The validate-roster request is a heavy POST; the badge
 * counts rows and validation is a secondary line, not a row — so it is
 * fired only while the panel is open (`usePersistentPanelOpen`), never for
 * the closed handle's badge.
 *
 * **Lead only.** The panel mounts by path on `/teams/*` and `/cycles/*`, so
 * a member can land on it; every lead-only read waits on the team role
 * (the `["team", …]` read the page itself makes) and a member gets no rows.
 *
 * Never throws: every read is `retry: false` (or the source's own policy);
 * a loading or errored read contributes no row.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { apiFetch } from "../../api/client";
import { useAllocationGaps } from "../../api/useAllocationGaps";
import { type CycleSummary, useCycle } from "../../api/useCycle";
import { useFixedShiftAsks } from "../../api/useFixedShiftAsks";
import { useUnboundCalls } from "../../api/useImports";
import { useLeaveReviewQueue } from "../../api/useLeaveReviewQueue";
import { useTeamMessages } from "../../api/useMemberMessages";
import { useCycleSwaps } from "../../api/useSwaps";
import { useTeam } from "../../api/useTeam";
import { useTeamFeature } from "../../api/useTeamFeature";
import { useAuth } from "../../lib/AuthContext";
import { type CycleHeaderFacts, rosterReleaseState } from "../../lib/cycleHeaderLine";
import { toISO } from "../../lib/dateWindow";
import { usePersistentPanelOpen } from "../../lib/persistentPanel";
import { defaultReviewPeriod } from "../../lib/reviewPeriod";
import { useUnpublishedEdits } from "../../lib/unpublishedEdits";
import { waitingCallsSpan } from "../../lib/waitingCalls";
import { useCoverageSummary } from "../CoverageSummarySection";
import { useSubmissionProgress } from "../SubmissionProgressSection";
import { useValidationSummary } from "../ValidationSummarySection";
import { type NextStepRow, type NextStepsFacts, buildNextSteps } from "./nextSteps";

/** The cycle payload's fields this hook reads beyond `useCycle`'s narrow type
 *  (the GET returns the full `CycleResponse`). */
type CycleFacts = CycleHeaderFacts & {
  team_id: string;
  submission_deadline?: string | null;
};

export interface TeamPeriodRow {
  id: string;
  horizon_start: string;
}

export interface NextSteps {
  /** `cycle` on a schedule's pages, `team` on a team's, `null` elsewhere. */
  scope: "cycle" | "team" | null;
  rows: NextStepRow[];
  /** A read the rows depend on is still in flight — the list waits rather
   *  than flashing its empty state. */
  loading: boolean;
}

/** `enabled: false` (the member panel's badge) makes no read at all. */
export function useNextSteps({ enabled = true }: { enabled?: boolean } = {}): NextSteps {
  const params = useParams<{ cycleId?: string; teamId?: string }>();
  const [panelOpen] = usePersistentPanelOpen();

  const routeCycleId = enabled ? params.cycleId : undefined;
  const routeTeamId = enabled ? params.teamId : undefined;
  const scope: NextSteps["scope"] = routeCycleId ? "cycle" : routeTeamId ? "team" : null;

  // The schedule (a cycle route) and the role on its team.
  const cycleQuery = useCycle(routeCycleId);
  const cycle = cycleQuery.data as (CycleSummary & CycleFacts) | undefined;
  const teamId = routeTeamId ?? cycle?.team_id;
  const teamQuery = useTeam(teamId);
  const role = teamQuery.data?.role;
  // A Taskmap team (`team_kind: "tasks"`) has no roster to take steps on —
  // its pages make none of these reads.
  const isTasksTeam = (teamQuery.data as { team_kind?: string } | undefined)?.team_kind === "tasks";
  const isLead = (role === "lead" || role === "co_lead") && !isTasksTeam;

  // Every lead-only read below keys off one of these two; `undefined`
  // disables it.
  const leadCycleId = isLead && scope === "cycle" ? routeCycleId : undefined;
  const leadTeamId = isLead && scope === "team" ? routeTeamId : undefined;

  const cycleSteps = useCycleStepFacts(leadCycleId, { validate: panelOpen });
  const teamSteps = useTeamStepFacts(leadTeamId);
  // Leave: this schedule's own queue; on a team page, the period the
  // Review tab opens on.
  const reviewCycleId = leadCycleId ?? teamSteps.reviewCycleId;
  const leaveSteps = useLeaveStepFacts(reviewCycleId);
  // #2077 — the team's unread messages from members, on either scope.
  const messageSteps = useMessageStepFacts(isLead && scope !== null ? teamId : undefined);
  // #2193 — members' fixed-shift asks waiting for a lead, on either scope.
  const askSteps = useFixedAskStepFacts(isLead && scope !== null ? teamId : undefined);

  const facts = useMemo<NextStepsFacts | null>(() => {
    if (scope === null || !isLead) return null;
    if (scope === "team") {
      return {
        scope,
        teamId,
        leave: leaveSteps.leave,
        ...messageSteps,
        ...askSteps,
        ...teamSteps.facts,
      };
    }
    return {
      scope,
      cycleId: routeCycleId,
      teamId,
      ...cycleSteps.facts,
      leave: leaveSteps.leave,
      ...messageSteps,
      ...askSteps,
    };
  }, [
    scope,
    isLead,
    teamId,
    routeCycleId,
    cycleSteps.facts,
    teamSteps.facts,
    leaveSteps.leave,
    messageSteps,
    askSteps,
  ]);

  const rows = useMemo(() => (facts ? buildNextSteps(facts) : []), [facts]);

  const loading =
    scope !== null &&
    (cycleQuery.isLoading ||
      teamQuery.isLoading ||
      cycleSteps.loading ||
      teamSteps.loading ||
      leaveSteps.loading);

  return { scope, rows, loading };
}

// ── The fact readers ──────────────────────────────────────────────────
//
// #2071 — split out of `useNextSteps` so the lead's phone Team home
// (`memberapp/team/useTeamHomeSteps.ts`) reads the same facts from the same
// hooks for the windows covering this week and next. Each takes an id the
// caller has already gated on the role; `undefined` makes no read.

/** The roster-scope facts one window contributes. */
export type CycleStepFacts = Pick<
  NextStepsFacts,
  | "shortDays"
  | "validationIssues"
  | "swapsHeld"
  | "unpublishedEdits"
  | "release"
  | "heldRuleProblems"
  | "pins"
  | "submissions"
>;

export interface CycleStepRead {
  /** `null` until the window's own payload has landed (or with no id). */
  facts: CycleStepFacts | null;
  /** The roster read's empty seats per day (published windows only). */
  shortfallsByDay: Record<string, number> | undefined;
  loading: boolean;
}

/** The roster rows' facts for one window (short days, swaps held, unpublished
 *  edits, the release step owed, pins, submissions). `validate` fires the
 *  heavy validation read (the laptop panel, only while it is open). */
export function useCycleStepFacts(
  cycleId: string | undefined,
  { validate = false }: { validate?: boolean } = {},
): CycleStepRead {
  const cycleQuery = useCycle(cycleId);
  const cycle = cycleQuery.data as (CycleSummary & CycleFacts) | undefined;
  const teamId = cycle?.team_id;
  const coverage = useCoverageSummary(cycleId);
  const validation = useValidationSummary(validate ? cycleId : undefined);
  const reportedEdits = useUnpublishedEdits(cycleId);
  const swapRequestsOn = useTeamFeature(cycleId ? teamId : undefined, "swap_requests");
  const swapsQuery = useCycleSwaps(cycleId, { enabled: swapRequestsOn === true });
  const gapsQuery = useAllocationGaps(cycleId ?? "", { enabled: Boolean(cycleId) });
  // Submissions only for a period that collects them: still open, with a
  // submission deadline set (there is no submissions component to gate on —
  // see the PR / USER_FLOW; the toolbar shows its count to every lead).
  const collectsSubmissions = cycle?.state === "open" && Boolean(cycle?.submission_deadline);
  const submissions = useSubmissionProgress(collectsSubmissions ? cycleId : undefined);

  const roster = coverage.rosterQuery.data;
  const facts = useMemo<CycleStepFacts | null>(() => {
    if (!cycleId) return null;
    const published = cycle?.state === "published";
    const shortDays = coverage.showsCount
      ? Object.entries(roster?.shortfalls_by_day ?? {})
          .filter(([, n]) => typeof n === "number" && n > 0)
          .map(([day]) => day)
      : undefined;
    const unpublished = published
      ? (reportedEdits ?? numberOr(roster?.unpublished_count, 0))
      : undefined;
    const gaps = gapsQuery.data;
    const gapRows = listOf<{ date: string; slot_id: string; seat_state: string }>(gaps?.gaps);
    const firstOpen = gapRows?.find((g) => g.seat_state === "open");
    const dash = submissions.dashboardQuery.data;
    return {
      shortDays,
      validationIssues: validation.showsCount ? validation.issueCount : undefined,
      swapsHeld:
        swapRequestsOn === true ? numberOr(swapsQuery.data?.state_summary?.accepted, 0) : undefined,
      unpublishedEdits: unpublished,
      release: cycle ? rosterReleaseState(cycle) : undefined,
      heldRuleProblems: cycle?.roster_held_rule_problems,
      pins: gaps
        ? {
            open: numberOr(gaps.open_count, 0),
            first: firstOpen ? { date: firstOpen.date, slot: firstOpen.slot_id } : undefined,
          }
        : undefined,
      submissions:
        collectsSubmissions && dash && !submissions.dashboardQuery.isError
          ? {
              submitted: numberOr(dash.submitted_count, 0),
              total: numberOr(dash.total_active_members, 0),
              deadlineDay: deadlineDay(cycle?.submission_deadline),
            }
          : undefined,
    };
  }, [
    cycleId,
    cycle,
    coverage.showsCount,
    roster,
    validation.showsCount,
    validation.issueCount,
    reportedEdits,
    swapRequestsOn,
    swapsQuery.data,
    gapsQuery.data,
    collectsSubmissions,
    submissions.dashboardQuery.data,
    submissions.dashboardQuery.isError,
  ]);

  const shortfallsByDay = roster?.shortfalls_by_day as Record<string, number> | undefined;
  const loading =
    cycleQuery.isLoading ||
    coverage.rosterQuery.isLoading ||
    swapsQuery.isLoading ||
    gapsQuery.isLoading;
  return { facts, shortfallsByDay, loading };
}

export interface TeamStepRead {
  facts: Pick<NextStepsFacts, "waitingCalls" | "noPeriod"> | null;
  /** The team's live windows list (`GET /api/teams/{id}/cycles`), when read. */
  periods: TeamPeriodRow[] | undefined;
  /** The period the Review tab opens on (newest first). */
  reviewCycleId: string | undefined;
  loading: boolean;
  isError: boolean;
}

/** The team rows' facts: imported calls waiting, nothing solved yet. */
export function useTeamStepFacts(teamId: string | undefined): TeamStepRead {
  const { accountId } = useAuth();
  // Same key + fetch as the team page's Schedule section and the
  // waiting-calls door, so the team page reads its periods once.
  const periodsQuery = useQuery({
    queryKey: ["team", teamId ?? "", "cycles", accountId],
    enabled: Boolean(teamId && accountId),
    retry: false,
    queryFn: () => apiFetch<TeamPeriodRow[]>(`/api/teams/${teamId}/cycles`),
  });
  const unboundQuery = useUnboundCalls(teamId ?? "");
  // Every list read is checked before it is walked: an unexpected body (an
  // older API, an error page) makes no row instead of throwing in the
  // shell, where a throw would take the whole page down.
  const periods = listOf<TeamPeriodRow>(periodsQuery.data);
  const facts = useMemo<TeamStepRead["facts"]>(() => {
    if (!teamId) return null;
    const unbound = unboundQuery.isError ? undefined : listOf<{ date: string }>(unboundQuery.data);
    const span = unbound ? waitingCallsSpan(unbound) : null;
    return {
      waitingCalls: span ?? undefined,
      noPeriod: periods?.length === 0 && unbound?.length === 0 ? true : undefined,
    };
  }, [teamId, periods, unboundQuery.data, unboundQuery.isError]);
  return {
    facts,
    periods,
    reviewCycleId: teamId ? defaultReviewPeriod(periods ?? [])?.id : undefined,
    loading: periodsQuery.isLoading || unboundQuery.isLoading,
    isError: periodsQuery.isError,
  };
}

/** The review queue's facts for one period. */
export function useLeaveStepFacts(reviewCycleId: string | undefined): {
  leave: NextStepsFacts["leave"];
  loading: boolean;
} {
  const leaveQuery = useLeaveReviewQueue(reviewCycleId);
  const leave = useMemo(
    () => leaveFacts(leaveQuery.data, reviewCycleId),
    [leaveQuery.data, reviewCycleId],
  );
  return { leave, loading: leaveQuery.isLoading };
}

/** #2077 — unread messages from members, and (#2071) who sent them. */
export function useMessageStepFacts(
  teamId: string | undefined,
): Pick<NextStepsFacts, "unreadMessages" | "messageSenders"> {
  const messagesQuery = useTeamMessages(teamId);
  const unreadMessages = messagesQuery.isError
    ? undefined
    : numberOr(messagesQuery.data?.unread_count, 0);
  // #2071 — who sent the unread ones (the row's second line), newest first.
  const messageSenders = useMemo(() => {
    const rows = listOf<{ sender_name?: string; read_at?: string | null; created_at?: string }>(
      messagesQuery.data?.messages,
    );
    if (!rows) return undefined;
    const names: string[] = [];
    for (const m of [...rows].sort((a, b) =>
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    )) {
      if (!m.read_at && m.sender_name && !names.includes(m.sender_name)) names.push(m.sender_name);
    }
    return names;
  }, [messagesQuery.data?.messages]);
  return useMemo(() => ({ unreadMessages, messageSenders }), [unreadMessages, messageSenders]);
}

/** #2193 — members' fixed-shift asks waiting for a lead, and the first one. */
export function useFixedAskStepFacts(
  teamId: string | undefined,
): Pick<NextStepsFacts, "fixedAsks"> {
  const asksQuery = useFixedShiftAsks(teamId, { enabled: Boolean(teamId) });
  const asks = asksQuery.data?.asks;
  return useMemo(() => {
    if (!asks) return { fixedAsks: undefined };
    const first = asks[0];
    return {
      fixedAsks: {
        count: asks.length,
        first: first ? { name: first.member_name, date: first.date } : undefined,
      },
    };
  }, [asks]);
}

function leaveFacts(
  queue: { rows: { status: string; urgent: boolean }[]; untouched_count: number } | undefined,
  cycleId: string | undefined,
): NextStepsFacts["leave"] {
  if (!queue || !cycleId) return undefined;
  // "To review" = not looked at yet (the queue's own `untouched_count`);
  // the urgent ones among them.
  const rows = listOf<{ status: string; urgent: boolean }>(queue.rows) ?? [];
  const urgent = rows.filter((r) => r.urgent && r.status === "indicated").length;
  return { toReview: numberOr(queue.untouched_count, 0), urgent, cycleId };
}

/** The value when it is an array, else `undefined`. */
function listOf<T>(value: unknown): T[] | undefined {
  return Array.isArray(value) ? (value as T[]) : undefined;
}

/** The value when it is a finite number, else `fallback`. */
function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** The deadline's local calendar day (`YYYY-MM-DD`), or `null`. */
function deadlineDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : toISO(at);
}
